import {
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text
} from '@chakra-ui/react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ResponsibleMedicalRecord } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/ResponsibleMedicalRecord'
import { ResponsibleMedicalRecordReview } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/ResponsibleMedicalRecordReview'
import { ResponsibleMedicalRecordView } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/ResponsibleMedicalRecordView'
import { ReviewResponsibleMedicalRecordForm } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/UpdateMedicalRecordDTO'
import { FoodRestrictionType } from '@/modules/operational/modules/restriction/domain/models/FoodRestrictionType'
import { CustomSelect } from '@/ui/components/CustomSelect'

import { MedicalRecordReviewType } from '../../../../constants/MedicalRecordReviewType'
import {
  ReviewDataCheckboxArrayValue,
  ReviewDataCheckboxSingleValue
} from '../components/ReviewDataCheckbox'
import {
  useReviewFormRepeatedMultipleArrayValues,
  useReviewFormRepeatedMultipleValues
} from '../components/ReviewFormRepeatedValue'
import ReviewStepWrapper from '../components/ReviewStepWrapper'

export function FoodReviewStep({
  data,
  mode
}:
  | {
      data: ResponsibleMedicalRecordView
      mode: typeof MedicalRecordReviewType.enum.VIEW
    }
  | {
      data: ResponsibleMedicalRecordReview
      mode: typeof MedicalRecordReviewType.enum.REVIEW
    }) {
  const form = useFormContext<ReviewResponsibleMedicalRecordForm>()

  useReviewFormRepeatedMultipleValues({
    disabled: mode === MedicalRecordReviewType.enum.VIEW,
    form,
    values: [
      {
        formProp: 'foods.patientSelective',
        responsibleValue: data.sendResponsible.foods.patientSelective,
        serverValue: data.registerPatient.foods.patientSelective
      }
    ]
  })

  useReviewFormRepeatedMultipleArrayValues({
    disabled: mode === MedicalRecordReviewType.enum.VIEW,
    form,
    formProp: 'foods.foods',
    serverValue: data.registerPatient.foods.foods,
    responsibleValue: data.sendResponsible.foods.foods,
    comparison: (a, b) =>
      !a
        ? false
        : !b
          ? false
          : a.food === b.food && a.foodRestrictionType === b.foodRestrictionType
  })

  return (
    <Flex justifyContent={'space-between'} gap={'6'} overflowX={'clip'}>
      <FoodReviewPart
        sourceData={data.sendResponsible}
        comparisonData={
          mode === 'REVIEW' ? data.registerPatient : form.getValues()
        }
        mode={mode}
      />
      <FoodReviewPart
        sourceData={data.registerPatient}
        comparisonData={
          mode === 'REVIEW' ? data.sendResponsible : form.getValues()
        }
        mode={mode}
      />
    </Flex>
  )
}

function FoodReviewPart({
  sourceData,
  comparisonData,
  mode
}: {
  sourceData: ResponsibleMedicalRecord
  comparisonData: ReviewResponsibleMedicalRecordForm | ResponsibleMedicalRecord
  mode: MedicalRecordReviewType
}) {
  const { t } = useTranslation()

  const form = useFormContext<ReviewResponsibleMedicalRecordForm>()
  const [foods, patientSelective] = form.watch([
    'foods.foods',
    'foods.patientSelective'
  ])
  const foodsFieldArray = useFieldArray({
    control: form.control,
    name: 'foods.foods'
  })

  return (
    <ReviewStepWrapper>
      <FormControl
        isInvalid={
          !!sourceData.foods?.patientSelective &&
          !!form.formState.errors.foods?.patientSelective?.message
        }
      >
        <FormLabel>Perfil do cliente</FormLabel>
        <CustomSelect
          options={[
            {
              label: t(
                'operational.food.restriction.updateModal.patientRestrictionSelective'
              ),
              value: 'YES'
            },
            {
              label: t(
                'operational.food.restriction.updateModal.patientRestrictionNonSelective'
              ),
              value: 'NO'
            }
          ]}
          value={sourceData.foods.patientSelective ?? undefined}
          disabled
        />
        <ReviewDataCheckboxSingleValue
          disabled={mode === MedicalRecordReviewType.enum.VIEW}
          checkboxProps={{ mt: '4' }}
          currentValue={sourceData.foods.patientSelective}
          serverValue={comparisonData.foods.patientSelective}
          formValue={patientSelective}
          set={(v) =>
            form.setValue('foods.patientSelective', v as 'YES' | 'NO')
          }
          reset={() => form.setValue('foods.patientSelective', undefined)}
        />
        <FormErrorMessage>
          {form.formState.errors.foods?.patientSelective?.message}
        </FormErrorMessage>
      </FormControl>
      <Divider />
      <Flex flexDir={'column'}>
        <Text
          fontSize={'1.125rem'}
          fontWeight={'500'}
          color={'custom.gray.100'}
        >
          {sourceData.foods.patientSelective === 'YES'
            ? 'Seletividade alimentar'
            : 'Restrição Alimentar'}
        </Text>
        <Text
          fontSize={'0.875rem'}
          fontWeight={'400'}
          color={'custom.gray.300'}
        >
          {sourceData.foods.patientSelective === 'YES'
            ? 'A seletividade alimentar, refere-se a um comportamento alimentar em que o indivíduo tem uma seleção limitada de alimentos que está disposto a comer.'
            : 'Perfil alimentar do cliente, com os alimentos restritos, preferidos e com a seletividade alimentar, caso tenha.'}
        </Text>
      </Flex>
      {sourceData.foods.foods.length === 0 ? (
        <Flex>
          <Text>Sem opções disponíveis</Text>
        </Flex>
      ) : (
        sourceData.foods.foods.map((food) => {
          return (
            <Flex key={food.id} alignItems={'flex-start'} flexDir={'column'}>
              <Flex gap={'4'} w={'full'}>
                <FormControl>
                  <FormLabel>Alimento</FormLabel>
                  <Input
                    isDisabled
                    value={food.food}
                    border={'solid 1px'}
                    borderColor={
                      food.food ? 'custom.gray.400' : 'custom.gray.600'
                    }
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Resposta ao alimento</FormLabel>
                  <Flex alignItems={'center'} gap={'4'}>
                    <CustomSelect
                      disabled
                      value={food.foodRestrictionType}
                      options={FoodRestrictionType.options.map(
                        (foodRestriction) => {
                          return {
                            label: t(
                              `pdf.models.restrictionType.${
                                foodRestriction.toLowerCase() as Lowercase<
                                  typeof foodRestriction
                                >
                              }`
                            ),
                            value: foodRestriction
                          }
                        }
                      )}
                    />
                  </Flex>
                </FormControl>
              </Flex>
              <ReviewDataCheckboxArrayValue
                disabled={mode === MedicalRecordReviewType.enum.VIEW}
                checkboxProps={{
                  mt: '4'
                }}
                parallelValues={comparisonData.foods.foods}
                currentValue={food}
                formValue={foods}
                set={(v) => foodsFieldArray.append(v)}
                reset={() =>
                  foodsFieldArray.remove(
                    foodsFieldArray.fields.findIndex(
                      (item) =>
                        item.food === food.food &&
                        item.foodRestrictionType === food.foodRestrictionType
                    ) ?? -1
                  )
                }
                comparison={(a, b) =>
                  a.food === b.food &&
                  a.foodRestrictionType === b.foodRestrictionType
                }
              />
            </Flex>
          )
        })
      )}
    </ReviewStepWrapper>
  )
}
